import {
  Accordion,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Spacer,
  Tag,
  Tbody,
  Tooltip,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { DownloadIcon, RepeatIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { auth } from "../../../core/auth";
import useApi from "../../../shared/hooks/useApi";
import { VisitInstanceDailyNoteId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import {
  formatErrorResponse,
  isServerErrorCode,
} from "../../../shared/utils/format-response-error";
import SignatureCanvas from "../../servicePlan/components/SignatureCanvas";
import type { AttestationListItem } from "../DailyNoteAttestationsRoute";
import { SummaryBox } from "./DailyNoteAttestationModal";
import CollapsibleSection from "./CollapsibleSection";
import { dispositionLabel } from "./dispositions";
import { useAttestationPdfDownload } from "./useAttestationPdfDownload";
import { useDailyNoteDocumentDownload } from "./useDailyNoteDocumentDownload";
import DailyNoteDetailsDrawer from "./DailyNoteDetailsDrawer";

/**
 * The co-signer's view of one batch: what the nurse signed, what they ruled, and the
 * separate declaration a case manager signs on top of it.
 *
 * A note the nurse signed that a newer revision has since replaced is marked but does
 * not block signing. The attestation records that a review happened at a point in
 * time; the newer revision separately re-enters the review queue. Blocking would
 * strand the batch for good, because a signature cannot be withdrawn.
 */
export default function DailyNoteCosignModal(props: {
  attestation: AttestationListItem | null;
  onClose: () => void;
}) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [confirmed, setConfirmed] = React.useState(false);

  // The co-signature prints on each note's PDF, so a co-signer with no signature on file is
  // refused (SIGNER_HAS_NO_SIGNATURE) and drawn one here, as in the signing modal.
  const [needsSignature, setNeedsSignature] = React.useState(false);
  const [signatureData, setSignatureData] = React.useState<string | null>(null);

  // Reading an attestation and counter-signing it are separate rights. The tab this
  // opens from is readable by anyone who can see daily notes, so the action has to
  // gate itself rather than rely on the page around it -- otherwise a reader is shown
  // a button the server will refuse.
  const canCosign = auth.isAllowedTo("cosign_visit_daily_note_attestation");
  // The re-upload endpoint demands the signing permission, so the control checks the
  // same one. Mirroring the server exactly is what stops a visible button from
  // answering 403.
  const canRequeueDocareUpload = auth.isAllowedTo("sign_visit_daily_note_attestation");

  const isOpen = props.attestation !== null;
  const isAwaiting = props.attestation?.status === "AWAITING_COSIGNATURE";

  // A batch from an office that never wanted a second reviewer is simply a signed
  // record. Framing it as a co-signature -- the title, the empty "Co-signed by" panel
  // -- described a step that was never part of its life.
  const involvesCosignature = props.attestation?.requiresCosignature === true;

  // Opened from a row in the manifest, so a reviewer can read the note itself without
  // leaving the record that cites it.
  const [openNoteId, setOpenNoteId] = React.useState<VisitInstanceDailyNoteId | null>(null);

  const pdf = useAttestationPdfDownload();
  const noteDocument = useDailyNoteDocumentDownload();

  // The query is keyed on the open batch and disabled while none is, so the id is
  // only ever read when there is one. `attestation` stands in when closed purely to
  // give the query key something well-formed.
  const attestation = props.attestation;

  const details = useQuery({
    ...queries.dailyNotes.attestationById(
      attestation?.id ?? (0 as unknown as AttestationListItem["id"])
    ),
    enabled: isOpen,
  });
  // The notes the nurse confirmed as concerning. Whether an incident was also raised is her
  // separate choice, so this is not the number of incidents.
  const concerningCount = (details.data?.items ?? []).filter(
    (item) => item.determination === "NON_ROUTINE"
  ).length;

  const declaration = useQuery({
    ...queries.dailyNotes.attestationDeclaration(),
    enabled: isOpen,
  });

  const requeueDocareUpload = useMutation({
    ...mutations.dailyNotes.requeueDocareUpload(),
    onSuccess: () => {
      toast({
        title: "Queued for DoCare",
        description:
          "The note will be published to the patient's DoCare profile shortly. Sending " +
          "it again replaces the stored document rather than adding another.",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNoteAttestations"] });
    },
    onError: (error) => {
      toast({
        title: "Could not queue the upload",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    },
  });

  const cosign = useMutation({
    ...mutations.dailyNotes.cosign(),
    onSuccess: (response) => {
      toast({
        title: `${response.noteCount} daily note${response.noteCount === 1 ? "" : "s"} co-signed`,
        status: "success",
        position: "top-right",
        isClosable: true,
      });
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNoteAttestations"] });
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNotes"] });
      props.onClose();
    },
    onError: (error) => {
      // The one refusal the co-signer can fix without leaving: reveal the pad instead of
      // reporting a dead end.
      if (isServerErrorCode(error, "SIGNER_HAS_NO_SIGNATURE")) {
        setNeedsSignature(true);
        toast({
          title: "A signature is needed first",
          description:
            "The co-signature prints your signature, and your profile has none on file. " +
            "Draw it below and this batch will be co-signed.",
          status: "info",
          position: "top-right",
          isClosable: true,
          duration: 9000,
        });
        return;
      }

      // `formatErrorResponse`, not `error.message`: the raw AxiosError reads only
      // "Request failed with status code 409", while the server's body names the
      // actual refusal -- already co-signed, or signed by this same person.
      toast({
        title: "Nothing was signed",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    },
  });

  const saveSignature = useMutation({
    ...mutations.dailyNotes.saveSignerSignature(),
    onSuccess: () => {
      // Straight back into co-signing: the box is already ticked, and asking again would
      // be asking the co-signer to confirm what they already confirmed.
      setNeedsSignature(false);
      onSign();
    },
    onError: (error) => {
      toast({
        title: "Could not save the signature",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    },
  });

  React.useEffect(() => {
    if (!isOpen) {
      setConfirmed(false);
      setNeedsSignature(false);
      setSignatureData(null);
    }
  }, [isOpen]);

  const member = auth.getAuthOrFail().data.agencyMember;

  // Why this reader cannot co-sign a waiting batch, mirroring the server's two refusals. When
  // there is one, the declaration, the checkbox and the button are all left out: they are the
  // co-signer's form, and showing it to someone who can only read invites a click that fails.
  const cosignBlockedReason = !canCosign
    ? "Only members allowed to co-sign daily notes can co-sign it."
    : details.data?.signedBy === member.id
    ? "You signed this batch, so the co-signature has to come from someone else."
    : null;
  const showCosignForm = isAwaiting && cosignBlockedReason === null;

  const onSign = () => {
    if (props.attestation === null || declaration.data === undefined) {
      return;
    }

    cosign.mutate({
      attestationId: props.attestation.id,
      body: {
        declarationVersion: declaration.data.cosignVersion,
        confirmed: true,
      },
    });
  };

  // One step up from 5xl, no custom width: the manifest is seven columns so it needed
  // more room than it had, but a table stretched across a large display is harder to
  // read across rather than easier.
  return (
    <Modal isOpen={isOpen} size="6xl" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader pr={20}>
          <Flex align="center" gap={3} justify="space-between">
            <Text>{involvesCosignature ? "Co-sign attestation" : "Attestation"}</Text>
            <Tooltip label="Download every note in this attestation as one PDF">
              <Button
                isDisabled={attestation !== null && pdf.isPending(attestation.id)}
                isLoading={attestation !== null && pdf.isPending(attestation.id)}
                leftIcon={<DownloadIcon />}
                loadingText="Preparing…"
                size="sm"
                variant="outline"
                onClick={() => {
                  if (attestation !== null) {
                    void pdf.downloadAll(attestation.id);
                  }
                }}
              >
                Download all
              </Button>
            </Tooltip>
          </Flex>
        </ModalHeader>

        <ModalBody>
          {details.data === undefined ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : (
            <Flex direction="column" gap={5}>
              <Box>
                <Text fontSize="md">
                  Signed by <b>{details.data.signedByName}</b> on{" "}
                  {dateFormatter.toDateTime(details.data.signedAt)}
                </Text>
              </Box>

              {/* The same tiles the signer saw when creating this, so the record reads
                  back the way it was made. Counts come from the frozen items, not from
                  live classification. */}
              {/* The first three are what the classifier observed. Concerning
                  is what the NURSE decided, and the only one of the four that sets
                  anything in motion downstream -- so it sits apart on the right with a
                  mark on it, rather than reading as a fourth tally beside Urgent. */}
              <Flex gap={4}>
                <SummaryBox accent="gray" label="Daily notes" value={details.data.items.length} />
                <SummaryBox
                  accent="red"
                  label="Urgent"
                  value={details.data.items.filter((item) => item.priority === "URGENT").length}
                />
                <SummaryBox
                  accent="orange"
                  label="High"
                  value={details.data.items.filter((item) => item.priority === "HIGH").length}
                />

                <Spacer />

                <SummaryBox
                  accent="red"
                  icon={
                    // Only when there is one. An alert mark over a zero draws the eye
                    // to the absence of a problem.
                    concerningCount === 0 ? undefined : (
                      <WarningTwoIcon boxSize={6} color="red.500" />
                    )
                  }
                  label="Concerning"
                  value={concerningCount}
                />
              </Flex>

              {/* Closed by default: it is the same fixed wording on every attestation,
                  so it is reference rather than something to read each time. It stays
                  one click away rather than behind a link, because it is the thing the
                  signature is actually against. */}
              <Accordion allowToggle={true} reduceMotion={true}>
                <CollapsibleSection
                  header={
                    <Text color="gray.600" fontSize="sm" textTransform="uppercase">
                      What the nurse signed
                    </Text>
                  }
                >
                  <Text fontSize="md" whiteSpace="pre-wrap">
                    {details.data.declarationText}
                  </Text>
                </CollapsibleSection>
              </Accordion>

              <Box maxHeight="300px" overflowY="auto">
                <Table size="md" variant="simple">
                  <Thead position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Patient</Th>
                      <Th>Visit date</Th>
                      <Th>Priority</Th>
                      <Th>Nurse ruling</Th>
                      <Th>Disposition</Th>
                      <Th>Summary</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {details.data.items.map((item) => (
                      <Tr
                        key={String(item.visitInstanceDailyNoteId)}
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                        onClick={() => setOpenNoteId(item.visitInstanceDailyNoteId)}
                      >
                        <Td>
                          {item.patientName}
                          {item.supersededSinceSigning ? (
                            <Tag colorScheme="yellow" ml={2} size="md">
                              revised since
                            </Tag>
                          ) : null}
                        </Td>
                        <Td whiteSpace="nowrap">{dateFormatter.toDateTime(item.visitStartTime)}</Td>
                        <Td>
                          {item.priority === "URGENT" || item.priority === "HIGH" ? (
                            <Tag
                              colorScheme={item.priority === "URGENT" ? "red" : "orange"}
                              size="md"
                            >
                              {item.priority}
                            </Tag>
                          ) : (
                            <Text color="gray.500">—</Text>
                          )}
                        </Td>
                        <Td>
                          {item.determination === null ? (
                            <Text color="gray.500">—</Text>
                          ) : (
                            <Text
                              color={item.determination === "NON_ROUTINE" ? "red.700" : "gray.700"}
                              fontSize="md"
                            >
                              {item.determination === "NON_ROUTINE"
                                ? "Concerning"
                                : "Not confirmed"}
                            </Text>
                          )}
                        </Td>
                        {/* Beside the ruling, because the two are the same person's
                            answer: what they decided, and what they did about it. */}
                        <Td>
                          {item.dispositions.length === 0 ? (
                            <Text color="gray.500">—</Text>
                          ) : (
                            <Flex direction="column" gap={1}>
                              {item.dispositions.map((disposition) => (
                                <Tag
                                  key={disposition}
                                  colorScheme="gray"
                                  size="md"
                                  variant="subtle"
                                  whiteSpace="nowrap"
                                >
                                  {dispositionLabel(disposition)}
                                </Tag>
                              ))}
                            </Flex>
                          )}
                        </Td>
                        <Td>
                          <Text fontSize="md">{item.summary ?? "—"}</Text>
                        </Td>
                        <Td>
                          {/* stopPropagation on both: the row itself opens the note
                              drawer, and neither of these should also do that. A
                              disabled-looking row action that silently opened a drawer
                              would be the worst of both. */}
                          <Flex gap={1}>
                            <Tooltip label="Download this daily note as a PDF">
                              <IconButton
                                aria-label="Download this daily note as a PDF"
                                icon={<DownloadIcon />}
                                isLoading={noteDocument.isPending(item.visitInstanceDailyNoteId)}
                                size="sm"
                                variant="ghost"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void noteDocument.downloadOne(item.visitInstanceDailyNoteId);
                                }}
                              />
                            </Tooltip>

                            {/* Only for someone who can sign: re-publishing a clinical
                                record to an external system is an act, not a read. */}
                            {canRequeueDocareUpload ? (
                              <Tooltip label="Send this daily note to DoCare again">
                                <IconButton
                                  aria-label="Send this daily note to DoCare again"
                                  icon={<RepeatIcon />}
                                  isLoading={
                                    requeueDocareUpload.isPending &&
                                    requeueDocareUpload.variables === item.visitInstanceDailyNoteId
                                  }
                                  size="sm"
                                  variant="ghost"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    requeueDocareUpload.mutate(item.visitInstanceDailyNoteId);
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {isAwaiting && cosignBlockedReason !== null ? (
                <Box bg="orange.50" borderRadius="md" p={3}>
                  <Text fontSize="md">
                    <b>Waiting for a co-signature.</b> {cosignBlockedReason}
                  </Text>
                </Box>
              ) : showCosignForm ? (
                <>
                  <Box bg="gray.50" borderRadius="md" p={4}>
                    {declaration.data === undefined ? (
                      <Spinner size="sm" />
                    ) : (
                      <Text fontSize="md" whiteSpace="pre-wrap">
                        {declaration.data.cosignText}
                      </Text>
                    )}
                  </Box>

                  <Flex direction="column" gap={1}>
                    <Text color="gray.600" fontSize="md">
                      Co-signing as
                    </Text>
                    <Text fontWeight="semibold">
                      {member.firstName} {member.lastName}
                    </Text>
                  </Flex>

                  {needsSignature && (
                    <Box borderColor="orange.300" borderRadius="md" borderWidth={1} p={4}>
                      <Flex align="center" gap={2} mb={2}>
                        <Text fontSize="md" fontWeight="semibold">
                          Your signature
                        </Text>
                        <Tag colorScheme="orange" size="sm">
                          Needs a signature
                        </Tag>
                      </Flex>

                      <Text color="gray.600" fontSize="md" mb={3}>
                        The co-signature prints your signature, and your profile has none saved.
                        Draw it once here — it is kept on your profile, so this is the only time you
                        will be asked.
                      </Text>

                      <SignatureCanvas
                        isDisabled={saveSignature.isPending}
                        value={signatureData}
                        onChange={setSignatureData}
                      />

                      <Flex align="center" gap={3} mt={3}>
                        <Button
                          colorScheme="blue"
                          isDisabled={signatureData === null}
                          isLoading={saveSignature.isPending}
                          onClick={() => {
                            if (signatureData === null) {
                              return;
                            }

                            saveSignature.mutate({ signatureData });
                          }}
                        >
                          Save signature and co-sign
                        </Button>

                        {signatureData === null && (
                          <Text color="gray.500" fontSize="sm">
                            Draw your signature above to continue.
                          </Text>
                        )}
                      </Flex>
                    </Box>
                  )}

                  <Checkbox
                    isChecked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  >
                    <Text fontSize="md">
                      I confirm my identity and intent to sign. This co-signature is recorded
                      against every daily note listed above.
                    </Text>
                  </Checkbox>
                </>
              ) : involvesCosignature ? (
                <Box bg="green.50" borderRadius="md" p={3}>
                  <Text fontSize="md">
                    Co-signed by <b>{details.data.cosignedByName ?? ""}</b>
                    {details.data.cosignedAt === null
                      ? ""
                      : ` on ${dateFormatter.toDateTime(details.data.cosignedAt)}`}
                  </Text>
                </Box>
              ) : null}
            </Flex>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={props.onClose}>
            {showCosignForm ? "Cancel" : "Close"}
          </Button>
          {showCosignForm ? (
            <Button
              colorScheme="blue"
              isDisabled={!confirmed || declaration.data === undefined || needsSignature}
              isLoading={cosign.isPending}
              onClick={onSign}
            >
              Co-sign
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>

      {/* Mounted inside the modal so it layers above it: a reviewer reading the record
          can open the note it cites without losing their place in the record. */}
      <DailyNoteDetailsDrawer
        visitInstanceDailyNoteId={openNoteId}
        onClose={() => setOpenNoteId(null)}
      />
    </Modal>
  );
}
